/** Who may open which Visitors Desk titles and tabs. Admins get everything; editors and writers
 *  get Articles and Issues for their own titles (the server enforces the same rule). */
import type { DeskPub } from '../api/visitorsDesk'
import type { DeskTab } from './deskPeriod'
import { DESK_PUBS, DESK_TABS, deskPath, isDeskPub, isDeskTab } from './deskTabs'
import { pubLabel } from './deskFormat'

export type DeskAccessMe = { role: string; sections?: { slug: string }[]; desk_tabs?: string[] }

const TITLES: DeskPub[] = DESK_PUBS.filter((p) => p !== 'all')
const EDITOR_TABS: DeskTab[] = ['articles', 'issues']

export function deskTabsOf(me: DeskAccessMe | null | undefined): DeskTab[] {
  if (!me) return []
  if (me.role === 'admin') return DESK_TABS.map((t) => t.id)
  return (me.desk_tabs ?? []).filter(isDeskTab)
}

export function deskPubsOf(me: DeskAccessMe | null | undefined): DeskPub[] {
  if (!me) return []
  if (me.role === 'admin') return [...DESK_PUBS]
  return (me.sections ?? []).map((s) => s.slug).filter((s): s is DeskPub => isDeskPub(s) && s !== 'all')
}

const EDITOR_PATH = /^\/visitors\/desk\/([a-z_]+)\/(articles|issues)(?:\/(\d+))?$/

/** True only for /visitors/desk/<own title>/(articles|issues)[/<id>], the tab being one the account has,
 *  and for the desk's user manual (/visitors/desk/help) once the account has any desk tab.
 *  A query string or hash is ignored; trailing slashes and extra segments are refused. */
export function editorMayOpen(path: string, me: DeskAccessMe | null | undefined): boolean {
  if (!me) return false
  const bare = path.split(/[?#]/)[0]
  if (bare === '/visitors/desk/help') return deskTabsOf(me).length > 0
  const m = EDITOR_PATH.exec(bare)
  if (!m) return false
  const pub = m[1]
  const tab = m[2] as DeskTab
  return isDeskPub(pub) && pub !== 'all' && deskPubsOf(me).includes(pub) && deskTabsOf(me).includes(tab)
}

/** Sidebar links for an editor or writer: Articles and Issues per title. */
export function editorDeskLinks(me: DeskAccessMe | null | undefined): { label: string; to: string }[] {
  const tabs = deskTabsOf(me).filter((t) => EDITOR_TABS.includes(t))
  const out: { label: string; to: string }[] = []
  for (const pub of deskPubsOf(me).filter((p) => TITLES.includes(p))) {
    for (const tab of tabs) out.push({ label: `${pubLabel(pub)} · ${tab === 'articles' ? 'Articles' : 'Issues'}`, to: deskPath(pub, tab) })
  }
  return out
}

/** Where to send a refused desk URL: the same tab or title when allowed, else the first allowed one. */
export function firstAllowed(me: DeskAccessMe | null | undefined, pub: string, tab: string): string | null {
  const pubs = deskPubsOf(me)
  const tabs = deskTabsOf(me)
  if (!pubs.length || !tabs.length) return null
  const p = isDeskPub(pub) && pubs.includes(pub) ? pub : pubs[0]
  const t = isDeskTab(tab) && tabs.includes(tab) ? tab : tabs[0]
  return deskPath(p, t)
}

const DESK_PATH = /^\/visitors\/desk(?=\/|$)(?:\/([^/]*))?(?:\/([^/]*))?/

/** Where the editor guard sends a refused /visitors/desk/... URL: the account's own Articles
 *  or Issues. Null for any other path, or when nothing on the desk is open to the account. */
export function editorDeskRedirect(path: string, me: DeskAccessMe | null | undefined): string | null {
  const m = DESK_PATH.exec(path.split(/[?#]/)[0])
  if (!m || !me) return null
  const to = firstAllowed(me, m[1] ?? '', m[2] ?? '')
  return to && editorMayOpen(to, me) ? to : null
}
